import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import {
  LayoutDashboard, Building2, Package, Users, Tags, UploadCloud,
  Search, Layers, Sparkles, Target, Megaphone, FileEdit, GitBranch,
  Kanban, FileText, Settings as Cog, LogOut, ChevronDown, FileType2,
  UserCog, FolderTree, GitCompareArrows, Database, TrendingDown, Ban,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useBrands } from "@/contexts/BrandContext";
import { api } from "@/lib/api";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

const NAV_GROUPS = [
  {
    label: "Overview",
    items: [
      { to: "/", icon: LayoutDashboard, label: "Dashboard", end: true, testid: "nav-dashboard" },
    ],
  },
  {
    label: "Intelligence",
    items: [
      { to: "/search-terms", icon: Search, label: "Search Terms", testid: "nav-search-terms" },
      { to: "/clusters-dashboard", icon: Layers, label: "Cluster Dashboard", testid: "nav-cluster-dashboard" },
      { to: "/product-opportunity", icon: Sparkles, label: "Product Opportunity", testid: "nav-product-opportunity" },
      { to: "/competitors-intel", icon: Target, label: "Competitor Intel", testid: "nav-competitor-intel" },
    ],
  },
  {
    label: "Execution",
    items: [
      { to: "/upload", icon: UploadCloud, label: "Weekly Upload", testid: "nav-upload" },
      { to: "/ads-performance", icon: TrendingDown, label: "Ad Performance", testid: "nav-ads-performance" },
      { to: "/bidding", icon: Megaphone, label: "Bidding", testid: "nav-bidding" },
      { to: "/listing", icon: FileEdit, label: "Listing Optimization", testid: "nav-listing" },
      { to: "/gaps", icon: GitBranch, label: "Product Gaps", testid: "nav-gaps" },
      { to: "/strategy", icon: Target, label: "Ads Strategy", testid: "nav-strategy" },
      { to: "/compare", icon: GitCompareArrows, label: "Compare Weeks", testid: "nav-compare" },
    ],
  },
  {
    label: "Masters",
    items: [
      { to: "/brands", icon: Building2, label: "Brands", testid: "nav-brands" },
      { to: "/products", icon: Package, label: "Products", testid: "nav-products" },
      { to: "/categories", icon: FolderTree, label: "Categories", testid: "nav-categories" },
      { to: "/competitors", icon: Users, label: "Competitors", testid: "nav-competitors" },
      { to: "/clusters", icon: Tags, label: "Keyword Clusters", testid: "nav-clusters" },
      { to: "/templates", icon: FileType2, label: "Marketplace Templates", testid: "nav-templates", adminOnly: true },
    ],
  },
  {
    label: "Reports",
    items: [
      { to: "/reports", icon: FileText, label: "Reports & Exports", testid: "nav-reports" },
      { to: "/users", icon: UserCog, label: "Users", testid: "nav-users", adminOnly: true },
      { to: "/data-management", icon: Database, label: "Data Management", testid: "nav-data-management", adminOnly: true },
      { to: "/repository", icon: Database, label: "Shared Repository", testid: "nav-repository", adminOnly: true },
      { to: "/category-negatives", icon: Ban, label: "Category Negatives", testid: "nav-category-negatives" },
      { to: "/settings", icon: Cog, label: "Settings", testid: "nav-settings", adminOnly: true },
    ],
  },
];

function Sidebar() {
  const { user } = useAuth();
  return (
    <aside
      data-testid="app-sidebar"
      className="fixed left-0 top-0 z-20 h-screen w-64 border-r border-slate-200 bg-[#F8FAFC] flex flex-col"
    >
      <div className="flex h-16 items-center gap-2 px-5 border-b border-slate-200">
        <div className="h-8 w-8 rounded-md bg-slate-900 flex items-center justify-center">
          <span className="text-white text-sm font-bold tracking-tight">M.</span>
        </div>
        <div className="flex flex-col leading-tight">
          <span className="text-sm font-bold text-slate-900 tracking-tight">Marketplace OS</span>
          <span className="text-[10px] uppercase tracking-widest text-slate-500">Intelligence</span>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
        {NAV_GROUPS.map((g) => (
          <div key={g.label}>
            <div className="px-3 mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-slate-400">
              {g.label}
            </div>
            <ul className="space-y-0.5">
              {g.items
                .filter((it) => !it.adminOnly || user?.role === "admin")
                .map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.end}
                    data-testid={item.testid}
                    className={({ isActive }) =>
                      cn(
                        "group flex items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors",
                        isActive
                          ? "bg-white text-slate-900 shadow-sm border border-slate-200/60"
                          : "text-slate-500 hover:text-slate-900 hover:bg-slate-100"
                      )
                    }
                  >
                    <item.icon strokeWidth={1.5} className="h-4 w-4" />
                    <span className="font-medium">{item.label}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
      <div className="px-4 py-3 border-t border-slate-200 text-[11px] text-slate-400">
        v0.3 · Phase 1–4 complete
      </div>
    </aside>
  );
}

function getCurrentISOWeek() {
  const today = new Date();
  const dayNum = today.getDay() || 7;
  const monday = new Date(today);
  monday.setDate(today.getDate() - dayNum + 1);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return {
    start: monday.toISOString().slice(0, 10),
    end: sunday.toISOString().slice(0, 10),
    label: `${monday.toISOString().slice(5, 10)} – ${sunday.toISOString().slice(5, 10)}`,
  };
}

// (kept for callers that still need a fallback)
export { getCurrentISOWeek };

function Topbar() {
  const { user, logout } = useAuth();
  const { brands, selectedBrandId, selectBrand, selectedBrand } = useBrands();
  const navigate = useNavigate();
  const [latestWeek, setLatestWeek] = useState(null);

  useEffect(() => {
    if (!selectedBrandId) { setLatestWeek(null); return; }
    api.get(`/uploads?brand_id=${selectedBrandId}`)
      .then((r) => {
        const proc = (r.data || []).find((u) => u.upload_status === "Processed");
        if (proc) setLatestWeek(`${proc.week_start_date} → ${proc.week_end_date}`);
        else setLatestWeek(null);
      })
      .catch(() => setLatestWeek(null));
  }, [selectedBrandId]);

  return (
    <header
      data-testid="app-topbar"
      className="sticky top-0 z-10 flex h-16 items-center justify-between border-b border-slate-200 bg-white/85 backdrop-blur px-6"
    >
      <div className="flex items-center gap-4">
        <div className="flex flex-col leading-tight">
          <span className="text-[11px] uppercase tracking-widest text-slate-400">Active Brand</span>
          <Select value={selectedBrandId} onValueChange={selectBrand}>
            <SelectTrigger
              data-testid="brand-selector"
              className="h-8 min-w-[220px] border-0 bg-transparent shadow-none px-0 text-base font-semibold tracking-tight text-slate-900 focus:ring-0"
            >
              <SelectValue placeholder="Select brand" />
            </SelectTrigger>
            <SelectContent>
              {brands.map((b) => (
                <SelectItem key={b.id} value={b.id} data-testid={`brand-option-${b.brand_name}`}>
                  {b.brand_name} · {b.marketplace}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="hidden md:flex flex-col leading-tight ml-6">
          <span className="text-[11px] uppercase tracking-widest text-slate-400">Latest reporting week</span>
          <span data-testid="topbar-week" className="text-sm font-semibold text-slate-900">
            {latestWeek || "No upload yet"}
          </span>
        </div>
        {selectedBrand?.category ? (
          <span className="hidden lg:inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-[11px] font-semibold tracking-wide text-slate-600 ml-4">
            {selectedBrand.category}
          </span>
        ) : null}
      </div>
      <div className="flex items-center gap-3">
        <DropdownMenu>
          <DropdownMenuTrigger
            data-testid="user-menu-trigger"
            className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 hover:bg-slate-50"
          >
            <div className="h-7 w-7 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs font-bold">
              {(user?.name || "?").slice(0, 1).toUpperCase()}
            </div>
            <div className="hidden sm:flex flex-col leading-tight items-start">
              <span className="text-xs font-semibold text-slate-900">{user?.name}</span>
              <span className="text-[10px] uppercase tracking-widest text-slate-500">
                {user?.role?.replace("_", " ")}
              </span>
            </div>
            <ChevronDown strokeWidth={1.5} className="h-3.5 w-3.5 text-slate-400" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid="menu-settings" onClick={() => navigate("/settings")}>
              <Cog className="h-4 w-4 mr-2" /> Settings
            </DropdownMenuItem>
            <DropdownMenuItem data-testid="menu-logout" onClick={async () => { await logout(); navigate("/login"); }}>
              <LogOut className="h-4 w-4 mr-2" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}

export default function AppLayout() {
  return (
    <div className="min-h-screen bg-[#FCFCFD]">
      <Sidebar />
      <div className="ml-64">
        <Topbar />
        <main className="p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
